import { toast } from "react-toastify";

import { ClaireNotificationsRegistry } from "components/NotificationsCenter/components/ClaireNotification";
import { NotificationsCenterQueryKeys } from "components/NotificationsCenter/queryKeys";
import { typedQueryClient, useTypedQuery, userKey } from "hooks";
import i18n from "utils/i18n";

import type { AppNotification } from "components/NotificationsCenter/queryKeys";
import type { Appointment, Pin } from "models";

export type NotificationType =
  | "AppointmentStatusNotification"
  | "AppointmentUpdateNotification"
  | "ExportNotification"
  | "KeyLockerCallForHelpNotification"
  | "PinStatusNotification"
  | "NewAppointmentNotification";

type ShowAppNotificationParams = {
  id: string;
  type: NotificationType;
  icon: string;
  time: string;
  title: string;
  description: string;
  pin?: Pin;
  path?: string;
  appointment?: Appointment;
  onNotificationClick?: () => void;
};

export const useAppNotifications = () => useTypedQuery({ queryKey: NotificationsCenterQueryKeys.notifications, initialData: [] }).data;

export const addAppNotification = (n: AppNotification) => typedQueryClient.setQueryData(NotificationsCenterQueryKeys.notifications, prev => [n, ...(prev ?? [])]);
export const removeAppNotification = (id: string) => typedQueryClient.setQueryData(NotificationsCenterQueryKeys.notifications, prev => prev?.filter(nt => nt.id !== id));
export const clearAppNotifications = () => typedQueryClient.setQueryData(NotificationsCenterQueryKeys.notifications, []);

const ClaireIcon = "/favicon.ico";

export const hasDesktopNotificationPermission = () => {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "denied") {
    toast.error(
      i18n.t("v8_for_desktop_notifications_enable_notifications_in_your_browser_settings", "For desktop notifications enable notifications in your browser settings")
    );
    return false;
  }

  if (Notification.permission === "default") {
    Notification.requestPermission();
    return false;
  }

  return true;
};

const showDesktopNotification = (title: string, description: string) => {
  if (!hasDesktopNotificationPermission()) return;
  new Notification(title, { body: description, icon: ClaireIcon });
};

export const showNotification = ({ id, pin, path, type, time, icon, title, description, appointment, onNotificationClick }: ShowAppNotificationParams) => {
  const user = typedQueryClient.getQueryData(userKey);

  const handleOnNotificationClick = () => {
    path && window.open(path, "_blank");
    onNotificationClick?.();
    removeAppNotification(id);
  };

  if (user?.is_desktop_notifications_enabled) showDesktopNotification(title, description);
  addAppNotification({ id, title, description, time, icon, onClick: handleOnNotificationClick });

  const config = ClaireNotificationsRegistry[type];

  toast(
    ({ closeToast }) =>
      config.render({
        icon,
        appointment,
        title,
        description,
        pin,
        path,
        closeToast: () => {
          closeToast();
          removeAppNotification(id);
        }
      }),
    { toastId: id, autoClose: 5000, closeButton: false, hideProgressBar: true, onClick: handleOnNotificationClick, ...config.toastOptions }
  );
};
